import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ExternalLink, BadgePercent, Sun, Sparkles } from "lucide-react";

export const VIVID_URL = "https://vividservicesaz.com/";

export const CROSS_BRAND_NOTE =
  "Valley Sun Screens customers qualify for special pricing with Vivid Services — mention your screen install to unlock member rates on window cleaning, exterior home maintenance, and Christmas & permanent lighting.";

function VividWordmark({ className = "" }: { className?: string }) {
  return (
    <span className={`font-black italic uppercase tracking-tight ${className}`}>
      Vivid <span className="text-[hsl(0,72%,50%)]">Services</span>
    </span>
  );
}

/** Compact "Our Home Service Companies" dropdown anchored to the header logo. */
export function BrandSwitcher({ dark = false }: { dark?: boolean }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Our home service companies"
        className="flex items-center gap-1 rounded-md bg-sun-yellow px-2.5 py-1 text-sm md:text-base font-black uppercase tracking-wide text-brand-red shadow-sm transition-transform hover:scale-[1.03]"
      >
        <span className="hidden sm:inline">Our Companies</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute left-0 top-full mt-2 w-[19rem] rounded-2xl border border-border bg-card p-3 shadow-2xl z-[70]">
          <p className="px-1 pb-2 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Our Home Service Companies
          </p>

          <Link
            to="/"
            onClick={() => setOpen(false)}
            className="flex items-start gap-3 rounded-xl p-2 hover:bg-accent transition-colors"
          >
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Sun className="w-5 h-5" />
            </span>
            <span className="flex flex-col">
              <span className="font-heading font-black text-sm text-foreground">Valley Sun Screens</span>
              <span className="text-xs text-muted-foreground">Custom solar screens &amp; repairs</span>
            </span>
          </Link>

          <a
            href={VIVID_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="flex items-start gap-3 rounded-xl p-2 hover:bg-accent transition-colors"
          >
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(0,72%,50%)]/10 text-[hsl(0,72%,50%)]">
              <Sparkles className="w-5 h-5" />
            </span>
            <span className="flex flex-col">
              <span className="flex items-center gap-1 font-heading text-sm text-foreground">
                <VividWordmark />
                <ExternalLink className="w-3 h-3 text-muted-foreground/70" aria-hidden />
              </span>
              <span className="text-xs text-muted-foreground">Window cleaning &amp; exterior home maintenance</span>
            </span>
          </a>

          <div className="mt-2 flex items-start gap-2 rounded-xl bg-secondary/25 p-2.5">
            <BadgePercent className="mt-0.5 w-4 h-4 shrink-0 text-primary" aria-hidden />
            <p className="text-[11px] leading-snug text-foreground/80">{CROSS_BRAND_NOTE}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/** Full-width cross-promo block for the footer / page sections. */
export function BrandFamilyPromo() {
  return (
    <section aria-labelledby="brand-family-heading" className="rounded-3xl border border-background/15 bg-background/5 p-6 md:p-8">
      <h3 id="brand-family-heading" className="font-heading font-black text-lg md:text-xl mb-1">
        Our Home Service Companies
      </h3>
      <p className="text-background/60 text-sm mb-6">Two Arizona brands, one team taking care of your home.</p>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl bg-background/10 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Sun className="w-5 h-5 text-sun-yellow" aria-hidden />
            <span className="font-heading font-black">Valley Sun Screens</span>
          </div>
          <p className="text-background/70 text-sm mb-3">
            Custom solar screens, rescreens and repairs across the West Valley.
          </p>
          <Link to="/instant-quote" className="text-sm font-bold text-sun-yellow hover:underline">
            Get an instant quote →
          </Link>
        </div>

        <div className="rounded-2xl bg-background/10 p-5">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-[hsl(0,72%,60%)]" aria-hidden />
            <VividWordmark className="text-background" />
          </div>
          <p className="text-background/70 text-sm mb-3">
            Window cleaning, exterior home maintenance, and Christmas &amp; permanent lighting.
          </p>
          <a
            href={VIVID_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-sm font-bold text-[hsl(0,72%,65%)] hover:underline"
          >
            Visit Vivid Services <ExternalLink className="w-3.5 h-3.5" aria-hidden />
          </a>
        </div>
      </div>

      <div className="mt-5 flex items-start gap-3 rounded-2xl bg-sun-yellow/15 p-4">
        <BadgePercent className="mt-0.5 w-5 h-5 shrink-0 text-sun-yellow" aria-hidden />
        <p className="text-sm text-background/85 leading-relaxed">
          <span className="font-bold">Customer perk:</span> {CROSS_BRAND_NOTE}
        </p>
      </div>
    </section>
  );
}
